import { load as yamlLoad } from "js-yaml";

// Runtime loaders for Decap-managed content living under /public/content.
// The build script writes an index.json into each folder listing filenames.

const BLOG_DIR = "/content/blog";
const PROJ_DIR = "/content/projects";

async function fetchIndex(dir) {
  const res = await fetch(`${dir}/index.json`, { cache: "no-store" });
  if (!res.ok) throw new Error(`Content index missing: ${dir}/index.json`);
  const { files } = await res.json();
  return files || [];
}

// -------------------- Blog (Markdown + frontmatter) --------------------

function parseFrontMatter(raw) {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { data: {}, body: raw };
  try {
    const data = yamlLoad(match[1]) || {};
    return { data, body: match[2] };
  } catch {
    return { data: {}, body: match[2] };
  }
}

export async function loadBlogList() {
  const files = await fetchIndex(BLOG_DIR);
  const posts = await Promise.all(
    files.map(async (file) => {
      const res = await fetch(`${BLOG_DIR}/${file}`, { cache: "no-store" });
      if (!res.ok) return null;
      const raw = await res.text();
      const { data } = parseFrontMatter(raw);
      const slug = data.slug || file.replace(/\.md$/i, "");
      return {
        slug,
        title: data.title || slug,
        date: data.date || null,
        author: data.author || "ElectroMax Team",
        cover: data.cover || null,
        excerpt: data.excerpt || "",
        tags: Array.isArray(data.tags) ? data.tags : [],
      };
    })
  );
  return posts
    .filter(Boolean)
    .sort((a, b) => (new Date(b.date || 0)) - (new Date(a.date || 0)));
}

export async function loadBlogPost(slug) {
  const files = await fetchIndex(BLOG_DIR);
  // slug may match file name or the front-matter slug field
  const candidates = files.filter(
    (f) => f.replace(/\.md$/i, "") === slug || f.endsWith(`${slug}.md`)
  );
  if (candidates.length === 0) return null;
  const filename = candidates[0];
  const res = await fetch(`${BLOG_DIR}/${filename}`, { cache: "no-store" });
  if (!res.ok) return null;
  const raw = await res.text();
  const { data, body } = parseFrontMatter(raw);
  return {
    slug: data.slug || slug,
    title: data.title || slug,
    date: data.date || null,
    author: data.author || "ElectroMax Team",
    cover: data.cover || null,
    excerpt: data.excerpt || "",
    tags: Array.isArray(data.tags) ? data.tags : [],
    body,
  };
}

// -------------------- Projects (JSON) --------------------

export async function loadProjectList() {
  const files = await fetchIndex(PROJ_DIR);
  const projects = await Promise.all(
    files.map(async (file) => {
      const res = await fetch(`${PROJ_DIR}/${file}`, { cache: "no-store" });
      if (!res.ok) return null;
      const p = await res.json();
      return { ...p, slug: p.slug || file.replace(/\.json$/i, "") };
    })
  );
  return projects
    .filter(Boolean)
    .sort((a, b) => {
      // Sort newest completed first: use completedYear + completedMonth
      const av = new Date(`${a.completedMonth || "January"} 1, ${a.completedYear || 1970}`);
      const bv = new Date(`${b.completedMonth || "January"} 1, ${b.completedYear || 1970}`);
      return bv - av;
    });
}

export async function loadProject(slug) {
  const files = await fetchIndex(PROJ_DIR);
  const candidates = files.filter(
    (f) => f.replace(/\.json$/i, "") === slug || f.endsWith(`${slug}.json`)
  );
  if (candidates.length === 0) return null;
  const filename = candidates[0];
  const res = await fetch(`${PROJ_DIR}/${filename}`, { cache: "no-store" });
  if (!res.ok) return null;
  const p = await res.json();
  return { ...p, slug: p.slug || slug };
}
